import type { WorkflowCase } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import { TertiaryButton } from '@enechain/ecloud-designsystem'
import { FaArrowLeftRegular } from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { iconSize } from '~/constants/iconSize'
import { useGetWorkflowCase } from '~/hooks/workflowCase/useGetWorkflowCase'
import { isWorkflowCaseNotFoundError } from '~/models/workflowCaseNotFound'
import { routePaths } from '~/routes/routePaths'

type Props = {
  /**
   * URL パスパラメータ `:caseId` から取得したケース UUID。
   * undefined の場合は WorkflowBackBar を非表示にする。
   */
  caseId: string | undefined
  /**
   * 業務ワークフローから開いた人に、 **この画面で何をすればよいか** を伝える 1〜2 文
   * (2026-10-07 デザイナー指摘「ケース詳細から開いた人がこの画面で何をしたら良いかを提示したい」)。
   * 対象日などケースの値を文言に入れるので、 ケースを受け取って文言を返す関数で渡す。
   * 画面ごとにやることが違うため、 渡した画面だけに出す (省略時は出さない)。
   * ワークフローが完了済みの時は出さない: 画面は読み取り専用 (AC-WFW-014) で
   * 「やること」 が無い。 完了日時・完了者も出さない (2026-10-20 デザイナー指示:
   * Alert に個別の値を並べない)。
   */
  guide?: (wfCase: WorkflowCase) => string
}

/**
 * ワークフローバックバー共通コンポーネント。
 *
 * caseId を受け取り、 useGetWorkflowCase でケース情報を fetch して
 * ページ上部に表示する。
 *
 * **Compact Alert (status="info") 1 行で描く** (UI 辞書 components/alert-placement.md §0「遷移元の文脈」
 * 2026-10-21。 それまでは DS Alert の縦積み: 2026-10-07 デザイナー指摘「DS alert に準拠したい」)。
 * 置き場所は `WorkflowPageLayout` が `PageLayout` の `notices` の先頭に置く (枠の端に寄せる)。
 * 8080 `js/workflow-back-bar.js` は 2026-08-03 に、 独自のバー (surface-primary + 下線 +
 * 左 3px のアクセント) から DS Alert (icon → 本文 → 右端の操作) へ移行済みで、 実装だけが
 * 旧形のまま残っていた。 status は info (ワークフローの進捗ではなく 「どこから来たか」 の告知)。
 *
 * 表示内容 (spec AC-WFW-013):
 * - タイトル (bold): 「対象のワークフロー ID は {ID} です。」
 * - 本文: 進行中なら `guide` (この画面でやること)。 完了済みなら本文なし (タイトルと戻る導線だけ)
 *
 * [spec 差異] spec AC-WFW-013 は完了済みの時に 「完了バッジ + 完了日時 + 完了者」 を出すとするが、
 * 2026-10-20 のデザイナー指示で出さない (Alert に状態・日時・実施者などの個別の値を並べない。
 * 遷移先の画面の他の項目と重複する。 UI 辞書 components/alert-placement.md)。
 * - 本文の真下: 「← 業務ワークフローに戻る」 (= ケース詳細画面 /workflow/cases/:caseId へ)
 *
 * [spec 差異] spec AC-WFW-013 は戻る導線を 「←」 アイコンのみ (ホバー説明・aria-label) と
 * するが、 8080 に合わせて文言付きのボタンにする。 アイコンのみにしたのは旧バーで
 * ヘッダー内が混み合うためで、 Alert の本文の下には文言を置く余地がある。
 *
 * caseId が undefined の場合と、ワークフローが見つからない場合 (AC-WFW-040) は
 * null を返す。
 *
 * fail-safe: 本バーは業務データではなくナビゲーション chrome なので、 ケース取得に
 * 失敗しても error boundary (= ErrorLayout = 全画面エラー) に飛ばさず、 caseId 先頭
 * 8 桁の fallback 表示で描画を続ける (= 下の displayId)。 global QueryClient の
 * `throwOnError: true` を本 query だけ `false` に上書きする。
 * ケース状態そのものを業務判断に使う画面 (= 完了済みなら read-only 等) は、 本バーとは
 * 別に自前の observer (通常は `useCurrentWorkflowCase`) で `useGetWorkflowCase` を
 * 購読して失敗時の扱いを決める (throwOnError は observer ごとに評価されるため、
 * ここでの上書きはそちらに波及しない。cache key ( `['workflowCase', caseId]` 相当)
 * は共有するため fetch 自体は重複しない)。
 */
export const WorkflowBackBar = ({
  caseId,
  guide,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const { data, error } = useGetWorkflowCase(caseId, { throwOnError: false })

  if (caseId === undefined) return null
  // ワークフローが見つからない時は出さない (spec AC-WFW-013 / 040。無効な ID を
  // ワークフロー ID として表示しない。戻る導線は WorkflowNotFoundView が担う)。
  // 遷移先画面の外枠 WorkflowPageLayout は本バーを常に置くので、表示の要否は
  // ここだけで決まる。
  if (isWorkflowCaseNotFoundError(error)) return null

  const wfCase = data?.workflowCase
  const isCompleted = wfCase?.completedAt !== undefined

  // workflowId は fetch 後に取得。 fetch 前はフォールバック表示。
  const displayId =
    wfCase !== undefined && wfCase.workflowId !== ''
      ? wfCase.workflowId
      : caseId.slice(0, 8).toUpperCase()

  const guideText =
    !isCompleted && wfCase !== undefined && guide !== undefined
      ? guide(wfCase)
      : undefined

  return (
    // 戻る導線は右端 (UI 辞書 components/alert-placement.md §0・§4。Tertiary sm + arrow-left、閉じる (×) は付けない)。
    // 外側の余白は持たない: 枠の端への寄せと次の通知・本文との間隔は PageLayout の `notices` が決める。
    <CompactAlert
      actions={
        <TertiaryButton asChild size="sm">
          {/* aria-label は表示文言と同じ。 遷移先 17 画面のテストが
              `getByLabelText('業務ワークフローに戻る')` で本バーの有無を判定しているため残す */}
          <RouterLink
            aria-label={t('page:workflowCaseDetail.action.backToListAriaLabel')}
            to={routePaths.workflowCaseDetail.path.replace(':caseId', caseId)}
          >
            <FaArrowLeftRegular
              boxSize={iconSize.gridInline}
              aria-hidden="true"
            />
            {t('page:workflowCaseDetail.action.backToListAriaLabel')}
          </RouterLink>
        </TertiaryButton>
      }
      // 完了済みの時は description を出さない (2026-10-20 デザイナー指示: Alert に状態・日時・
      // 実施者などの個別の値を並べない。 画面の他の項目と重複する。 UI 辞書 alert-placement.md)。
      // 読み取り専用の理由は、 非活性にした操作のツールチップが伝える (AC-WFW-014)。
      description={guideText}
      // 「対象のワークフロー ID は … です。」 を 1 文のタイトル (bold) にする
      // (2026-10-07 デザイナー指示)。 ラベルと値に分けず文にするのは、 遷移先の画面が
      // **どのワークフローの作業か** を読み下せるようにするため。
      status="info"
      title={t('page:workflowCaseDetail.header.backBarTitle', {
        workflowId: displayId,
      })}
    />
  )
}
